// Angular
import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

// SyncFusion
import { GridComponent } from '@syncfusion/ej2-angular-grids';

// Cerium
import { GridDesign } from './cer-grid-design';
import { CerGridCommandService } from './cer-grid-command.service';
import { CerGridSelectionService } from './cer-grid-selection.service';
import { UiActionTypeEnum, UiCommandEvent, UiCommandSourceEnum } from '@ui/cer-ui-types';

@Injectable()
export class CerGridVirtualizeService implements OnDestroy {
  // Subscriptions
  private subscriptionManager: Subscription = new Subscription();

  // Grid edit
  public grid: GridComponent;
  public design: GridDesign;

  // Constructor
  constructor(private command: CerGridCommandService,
    private selection: CerGridSelectionService) {
    this.subscriptionManager.add(command.commandPre$.subscribe(cmd => this.onCommandPre(cmd)));
  }

  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }

  public init(grid: GridComponent, design: GridDesign) {
    this.grid = grid;
    this.design = design;
    //this.grid.pageSettings = { pageSizes: false, pageSize: d.pageSize };
    this.doReflow(true);
  }

  public onCommandPre(event: UiCommandEvent) {
    if (!this.design?.useMaxHeight) {
      return;
    }
    switch (event.source) {
      case UiCommandSourceEnum.Command:
        switch (event.actionType) {
          case UiActionTypeEnum.Reflow:
            this.doReflow();
            break;
        }
        break;
      case UiCommandSourceEnum.ActionBegin:
        switch (event.actionType) {
          case UiActionTypeEnum.VirtualScroll:
            this.onVirtualScrollActionBegin(event.args);
            break;
        }
        break;
      case UiCommandSourceEnum.ActionComplete:
        switch (event.actionType) {
          case UiActionTypeEnum.DataBound:
            this.doReflow(true);
            break;
          case UiActionTypeEnum.Loaded:
            this.doReflow(true);
            break;
          case UiActionTypeEnum.Created:
            this.doReflow();
            break;
          case UiActionTypeEnum.VirtualScroll:
            this.onVirtualScrollActionComplete(event.args);
            break;
        }
        break;
    }
  }

  private onVirtualScrollActionBegin(args: any) {
  }

  private onVirtualScrollActionComplete(args: any) {
  }


  public doReflow(initialLoad: boolean = false) {
    if (this.grid && this.design?.useMaxHeight) {
      var panel: HTMLDivElement = this.setupMaxHeight(initialLoad);
      if (panel && this.design?.pagingVirtual) {
        this.setupVirtualTable(panel);
      }
    }
  }

  private setupVirtualTable(panel: HTMLDivElement) {
    var virtualtables = panel?.getElementsByClassName('e-virtualtable');
    if (virtualtables?.length) {
      var virtualTable = <HTMLDivElement>virtualtables[0];
      if (virtualTable) {
        var hpxVT = panel.clientHeight + 'px';
        // If horizontal scrollbar needed (column overflow), then allow space for horizontal scrollbar to avoid vertical overflow
        if (panel && virtualTable.clientWidth > panel.clientWidth) {
          // Deduct height of visible scrollbar-x from virtual table to avoid unneeded scrollbar-y
          var barHeight: number = 16; //.offsetHeight - panel.clientHeight;
          hpxVT = (panel.clientHeight - barHeight) + 'px';
        }
        if (virtualTable.style.minHeight != hpxVT) {
          virtualTable.style.minHeight = hpxVT;
          //this.debug("VT min height: " + hpxVT);
        }
      }

      // Virtual track
      var virtualtracks = panel.getElementsByClassName('e-virtualtrack');
      if (virtualtracks?.length > 0) {
        var virtualtrack = <HTMLDivElement>virtualtracks[0];
        if (virtualtrack.clientHeight > panel.clientHeight) {
          //  virtualtrack.style.height = panel.style.height;
          //this.debug("Track height: " + virtualtrack.style.height);
        }
        /*
        if (virtualtracks.length == 1) {
          if (virtualtrack.clientHeight < panel.clientHeight) {

          }
        } 
          */
      }
    }
  }

  public setupMaxHeight(initialLoad: boolean): HTMLDivElement {
    const e = this.grid.element;
    if (e != null && e.parentElement.clientHeight > 0) {
      /* Header */
      var exclude: number = e?.querySelector('.e-headercontent')?.clientHeight ?? 36;
      // Toolbar
      exclude += e.querySelector('.e-toolbar')?.clientHeight ?? 48;
      // Footer
      if (this.grid.aggregates?.length > 0) {
        var aggRows = e?.querySelectorAll('.e-summaryrow');
        if (aggRows.length > 0) {
          let am: number = aggRows.length;
          for (var ai = 0; ai < am; ai++) {
            exclude += aggRows[ai].clientHeight; // Height of aggregate row
          }
        }
        else {
          exclude += 36 * this.grid.aggregates.length;
        }
      }
      if (this.grid.groupSettings?.showDropArea) {
        exclude += 48;
      }
      // Pager
      if (this.design.allowPaging) {
        exclude += e.querySelector('.e-gridpager')?.clientHeight ?? 43;
      }

      var h = e.parentElement.clientHeight;
      h = Math.trunc(h);
      var hpx: string = h + "px";
      if (this.grid.height != hpx) {
        (<any>this.grid).properties["height"] = hpx;
        e.style.height = hpx;
      }

      h = e.parentElement.clientHeight - exclude; // 127 = height toolbar + header + pager
      h = Math.trunc(h - 1);
      var panel: HTMLDivElement = <HTMLDivElement>this.grid.contentModule?.getPanel();
      if (panel) {
        // Set Height Content
        hpx = h + "px";
        if (panel.style.height != hpx) {
          panel.style.height = hpx;
          //this.debug("Panel height: " + panel.style.height);
        }
      }
      const rowHeight = this.grid?.rowHeight ?? 36;
      if (this.design.fixedHugePage) {
        this.setPageSize(1000,true);
      }
      if (this.design?.allowPaging || this.design?.pagingVirtual == true) {
        h -= 12; // Deduct scroll bar height
        var pageSize: number = Math.floor(h / rowHeight);
        if (this.design?.pagingVirtual == true) {
          pageSize = pageSize * 2; 
        }
        this.setPageSize(pageSize, initialLoad);
      }
    }
    return panel;
  }

  private setPageSize(pageSize: number, initialLoad: boolean) {
    if (pageSize >= 1 && this.grid.pageSettings.pageSize != pageSize) {
      this.selection.rowSelectedSave();
      this.selection.rowSelectedRestore(pageSize);
      if (initialLoad) {
        (<any>this.grid.pageSettings).properties["pageSize"] = pageSize;
        if (this.grid.pagerModule?.pagerObj) {
          (<any>this.grid.pagerModule.pagerObj).properties["pageSize"] = pageSize;
        }
      }
      else {
        this.grid.pageSettings.pageSize = pageSize;
      }
    }
  }
}
